import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSignalAction } from './useSignalAction'
import type { NextAction } from '../types/scheduleList.types'
import { useInvalidateSignals } from './useTaskSignals'
import { MATERIAL_ORDERS_KEY } from './useMaterialOrders'
import { TASK_PROGRESS_KEY } from './useTaskProgress'
import { SCHEDULE_LIST_KEY } from './useScheduleListData'
import type { ListPopup } from './useScheduleListState'
import type { GanttDeepLink } from '../types/gantt.types'

// 리스트 보기 동작 (구현계획 C-03) — 다음 할 일·간트 링크 실행, 팝업 변경 후 쿼리 무효화.
// 처리 버튼은 서버 주소로 처리한다(useSignalAction). 서버 주소가 이 현장의 간트 팝업 주소(pop)면 리스트가 가진 같은 팝업을 그 자리에서 연다:
//  pt-assign = 시공파트너배정 팝업 · sup-order = 자재파트너배정 팝업(focusTaskId) · ship = 배송정보현황 팝업(focusTaskId) — 리스트가 직접 호스팅(D-10)
//  그 밖의 주소는 호스트가 이동한다.
//  간트 상세창 = openTaskDetail(E-02 의 NavFn 확장을 호출 쪽이 연결해 넘김)
// 팝업을 여는 상태는 C-02(useScheduleListState)가 가지고, 이 훅은 열기 함수만 받는다.

export interface UseScheduleListActionsArgs {
  /** C-02 의 openPopup */
  openPopup: (p: ListPopup) => void
  /** 현재 현장 — 서버 주소의 siteId 가 다르면 이 화면이 열지 않고 호스트로 넘긴다 */
  siteId?: number
  /** 간트 보기에서 해당 공종 상세창을 연다(E-02). 주입이 없으면 canOpenTaskDetail 이 false */
  openTaskDetail?: (taskId: number, card?: GanttDeepLink['card']) => void
}

export function useScheduleListActions({ openPopup, siteId, openTaskDetail }: UseScheduleListActionsArgs) {
  const qc = useQueryClient()
  const invalidateSignals = useInvalidateSignals()

  const signalAction = useSignalAction(link => {
    if (siteId == null || (link.siteId !== null && link.siteId !== siteId)) return false
    if (link.pop === 'pt-assign') { openPopup({ kind: 'sigong' }); return true }
    if (link.pop === 'sup-order') { openPopup({ kind: 'material', focusTaskId: link.taskId ?? null }); return true }
    if (link.pop === 'ship') { openPopup({ kind: 'ship', focusTaskId: link.taskId ?? null }); return true }
    return false
  }, siteId)

  /** 다음 할 일 버튼을 보일 수 있는지(없으면 버튼 숨김) */
  const canRunNextAction = useCallback((action: NextAction | null) => !!action && signalAction.canAct(action.action), [signalAction])

  /** 다음 할 일 버튼 실행 */
  const runNextAction = useCallback((_taskId: number, action: NextAction) => { signalAction.act(action.action) }, [signalAction])

  /** [공종 상세창에서 수정 ›] — 간트 상세창으로 이동 */
  const openGanttDetail = useCallback((taskId: number, card?: GanttDeepLink['card']) => { openTaskDetail?.(taskId, card) }, [openTaskDetail])
  const canOpenTaskDetail = openTaskDetail != null

  /**
   * 팝업에서 변경이 일어난 뒤 호출 — 신호(+오늘 할일·현장 신호·배송 현황)와 공종·발주·진행 요약 쿼리를 무효화한다.
   * 리스트는 낙관 갱신을 하지 않고 재조회 결과를 그대로 그린다.
   */
  const afterChange = useCallback(() => Promise.all([
    invalidateSignals(),
    qc.invalidateQueries({ queryKey: [SCHEDULE_LIST_KEY] }),
    qc.invalidateQueries({ queryKey: [MATERIAL_ORDERS_KEY] }),
    qc.invalidateQueries({ queryKey: [TASK_PROGRESS_KEY] }),
  ]), [invalidateSignals, qc])

  return { canRunNextAction, signalAction, runNextAction, openGanttDetail, canOpenTaskDetail, afterChange }
}

export type ScheduleListActions = ReturnType<typeof useScheduleListActions>
